import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrgPermissions } from '@/hooks/useOrgPermissions';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Eye, Edit, Trash2 } from 'lucide-react';

interface ModeloContrato {
  id: string;
  nome_modelo: string;
  tipo_contrato_id: string;
  conteudo_html: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  tipo_contrato?: {
    nome: string;
  };
}

export default function ModelosContrato() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userOrgInfo } = useOrgPermissions();
  const { hasPermission, refetch, loading: permissionsLoading } = usePermissions();
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  const canRead = hasPermission('recursos_humanos', 'modelos_contrato', 'read');
  const canWrite = hasPermission('recursos_humanos', 'modelos_contrato', 'write');
  const canAdmin = hasPermission('recursos_humanos', 'modelos_contrato', 'admin');

  console.log('ModelosContrato - Permissions:', { canRead, canWrite, canAdmin, permissionsLoading, permissionsChecked });

  useEffect(() => {
    // Refazer o fetch das permissões quando o componente carregar
    console.log('ModelosContrato - Calling refetch...');
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked || permissionsLoading) return;
    
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchModelos();
  }, [canRead, permissionsChecked, permissionsLoading]);

  const fetchModelos = async () => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select(`
          *,
          tipo_contrato!fk_tbl_modelo_contrato_tipo_contrato(nome)
        `)
        .eq('status', 'ativo')
        .eq('org_id', userOrgInfo.orgId) // Filtrar apenas pela organização do usuário
        .order('nome_modelo');

      if (error) throw error;

      setModelos((data as any) || []);
    } catch (error) {
      console.error('Erro ao buscar modelos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar modelos de contrato',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!canAdmin) return;

    if (!confirm('Tem certeza que deseja excluir este modelo de contrato?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('tbl_modelo_contrato')
        .update({
          status: 'inativo',
          updated_by: user?.id
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Sucesso',
        description: 'Modelo de contrato excluído com sucesso'
      });

      fetchModelos();
    } catch (error) {
      console.error('Erro ao excluir modelo:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir modelo de contrato',
        variant: 'destructive'
      });
    }
  };

  const filteredModelos = modelos.filter(modelo =>
    modelo.nome_modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    modelo.tipo_contrato?.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!canRead) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Acesso Negado</CardTitle>
          <CardDescription>
            Você não tem permissão para acessar modelos de contrato.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Modelos de Contrato</h1>
          <p className="text-muted-foreground">
            Gerencie os modelos de contrato do sistema
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => navigate('/intranet/recursos-humanos/modelos-contrato/novo')}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Modelo
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4" />
            <Input
              placeholder="Buscar por nome ou tipo de contrato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : filteredModelos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'Nenhum modelo encontrado' : 'Nenhum modelo cadastrado'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Modelo</TableHead>
                  <TableHead>Tipo de Contrato</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModelos.map((modelo) => (
                  <TableRow key={modelo.id}>
                    <TableCell className="font-medium">
                      {modelo.nome_modelo}
                    </TableCell>
                    <TableCell>
                      {modelo.tipo_contrato?.nome}
                    </TableCell>
                    <TableCell>
                      <Badge variant={modelo.status === 'ativo' ? "default" : "secondary"}>
                        {modelo.status === 'ativo' ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(modelo.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/intranet/recursos-humanos/modelos-contrato/${modelo.id}/visualizar`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/intranet/recursos-humanos/modelos-contrato/${modelo.id}/editar`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(modelo.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}